import React, { useState, useEffect, useMemo } from 'react';
import API from '../services/api';
import '../styles/Dashboard.css';

export default function WardenDashboard() {
  /* master data */
  const [requests, setRequests] = useState([]);

  /* pagination – pending table */
  const [page, setPage] = useState(1);
  const itemsPerPage = 50;

  /* previous-requests toggle + pagination */
  const [showPrev, setShowPrev] = useState(false);
  const [prevPage, setPrevPage] = useState(1);
  const prevItemsPerPage = 50;

  /* search for pending table */
  const [search, setSearch] = useState('');

  /* load all requests once */
  useEffect(() => {
    API.get('/requests/warden').then(r => setRequests(r.data));
  }, []);

  /* split datasets */
  const pendingList  = useMemo(
    () => requests.filter(r => r.status === 'pending'),
    [requests]
  );
  const prevList     = useMemo(
    () => requests.filter(r => r.status === 'approved' || r.status === 'rejected'),
    [requests]
  );

  /* counters */
  const pendingCount  = pendingList.length;
  const approvedCount = prevList.filter(r => r.status === 'approved').length;
  const rejectedCount = prevList.filter(r => r.status === 'rejected').length;

  /* filtered + sorted pending table */
  const filteredPending = useMemo(() => {
    const q = search.toLowerCase();
    return pendingList
      .filter(r =>
        [r.name, r.studentId, r.leaveFor, r.transport]
          .some(f => f.toLowerCase().includes(q))
      )
      .sort((a,b)=>new Date(b.dateTime)-new Date(a.dateTime));
  }, [pendingList, search]);

  /* pagination slices */
  const totalPages = Math.max(1, Math.ceil(filteredPending.length / itemsPerPage));
  const pageData   = filteredPending.slice((page-1)*itemsPerPage, page*itemsPerPage);

  const prevTotal  = Math.max(1, Math.ceil(prevList.length / prevItemsPerPage));
  const prevData   = prevList
                      .sort((a,b)=>new Date(b.dateTime)-new Date(a.dateTime))
                      .slice((prevPage-1)*prevItemsPerPage, prevPage*prevItemsPerPage);

  /* modal state */
  const [active, setActive] = useState(null);
  const [saving, setSaving] = useState(false);

  /* save changes */
  const save = async () => {
    setSaving(true);
    try {
      await API.put(`/requests/warden/${active._id}`, {
        status: active.status,
        wardenComment: active.wardenComment,
      });
      setActive(null);
      /* refresh master list (cheap but robust) */
      const { data } = await API.get('/requests/warden');
      setRequests(data);
    } catch (err) {
      alert(err.response?.data?.msg || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  /* ───────── UI ───────── */
  return (
    <div className="dashboard-container">
      {/* header */}
      <div className="dashboard-header">
        <h2>🛎️ Warden Dashboard</h2>
<div className="counters">
  <div className="counter-item counter-pending">
    <div>Pending</div><div>{pendingCount}</div>
  </div>

  <div className="counter-item counter-approved">
    <div>Approved</div><div>{approvedCount}</div>
  </div>

  <div className="counter-item counter-rejected">
    <div>Rejected</div><div>{rejectedCount}</div>
  </div>
</div>
      </div>

      {/* search bar */}
      <div className="search-filter">
        <input
          type="text"
          placeholder="Search pending requests…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {/* PENDING table */}
      <h3>Pending Requests</h3>
      <div className="table-container prettified">
        <table>
          <thead>
            <tr>
              <th>Student</th>
              <th>ID</th>
              <th>Date & Time</th>
              <th>Reason</th>
              <th>Transport</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {pageData.length ? pageData.map(r=>(
              <tr key={r._id}>
                <td>{r.name}</td>
                <td>{r.studentId}</td>
                <td>{new Date(r.dateTime).toLocaleString()}</td>
                <td>{r.leaveFor}</td>
                <td>{r.transport}</td>
                <td><button className="view-btn" onClick={()=>setActive(r)}>View</button></td>
              </tr>
            )) : (
              <tr><td colSpan={6} style={{textAlign:'center',padding:16}}>No pending requests.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {/* pagination */}
      <div className="pagination">
        <button onClick={()=>setPage(p=>Math.max(1,p-1))} disabled={page===1}>Prev</button>
        <span className="page-info">Page {page} of {totalPages}</span>
        <button onClick={()=>setPage(p=>Math.min(totalPages,p+1))} disabled={page===totalPages}>Next</button>
      </div>

      {/* toggle previous */}
      <div style={{marginTop:24}}>
        <button className="action-btn" onClick={()=>setShowPrev(v=>!v)}>
          {showPrev ? 'Hide Previous Requests' : 'Show Previous Requests'}
        </button>
      </div>

      {/* PREVIOUS table */}
      {showPrev && (
        <>
          <h3 style={{marginTop:16}}>Approved & Rejected</h3>
          <div className="table-container prettified">
            <table>
              <thead>
                <tr>
                  <th>Student</th>
                  <th>ID</th>
                  <th>Date & Time</th>
                  <th>Reason</th>
                  <th>Transport</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {prevData.length ? prevData.map(r=>(
                  <tr key={r._id}>
                    <td>{r.name}</td>
                    <td>{r.studentId}</td>
                    <td>{new Date(r.dateTime).toLocaleString()}</td>
                    <td>{r.leaveFor}</td>
                    <td>{r.transport}</td>
                    <td>
                      <span className={`status-badge status-${r.status}`}>{r.status}</span>
                    </td>
                    <td><button className="view-btn" onClick={()=>setActive(r)}>View</button></td>
                  </tr>
                )) : (
                  <tr><td colSpan={7} style={{textAlign:'center',padding:16}}>No records.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          {/* pagination for previous */}
          <div className="pagination">
            <button onClick={()=>setPrevPage(p=>Math.max(1,p-1))} disabled={prevPage===1}>Prev</button>
            <span className="page-info">Page {prevPage} of {prevTotal}</span>
            <button onClick={()=>setPrevPage(p=>Math.min(prevTotal,p+1))} disabled={prevPage===prevTotal}>Next</button>
          </div>
        </>
      )}

      {/* modal */}
      {active && (
        <div style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: '#fff',
            borderRadius: '12px',
            maxWidth: '600px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            animation: 'slideUp 0.3s ease-out'
          }}>
            <div style={{
              padding: '24px',
              borderBottom: '1px solid #e5e7eb',
              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
              borderRadius: '12px 12px 0 0'
            }}>
              <h3 style={{margin: 0, color: '#fff', fontSize: '20px', fontWeight: '600'}}>Request Details</h3>
            </div>
            <div style={{padding: '24px'}}>
              {/* details table */}
              {[
                ['Student', active.name],
                ['ID', active.studentId],
                ['Email', active.email],
                ['Date & Time', new Date(active.dateTime).toLocaleString()],
                ['Reason', active.leaveFor],
                ['Leaving With', active.pickUpWith],
                ['Transport', active.transport],
                ...(active.transport==='private'
                  ? [['Vehicle', active.vehicleNo], ['Driver', active.driverName]]
                  : [])
              ].map(([lbl,val])=>(
                <div key={lbl} style={{
                  display: 'grid',
                  gridTemplateColumns: '140px 1fr',
                  gap: '12px',
                  padding: '12px 0',
                  borderBottom: '1px solid #f3f4f6'
                }}>
                  <label style={{fontWeight: '600', color: '#4b5563'}}>{lbl}</label>
                  <div style={{color: '#1f2937'}}>{val}</div>
                </div>
              ))}

              <div style={{marginTop: '20px'}}>
                <label style={{display: 'block', fontWeight: '600', color: '#4b5563', marginBottom: '8px'}}>Status</label>
                <select
                  value={active.status}
                  onChange={e=>setActive({...active,status:e.target.value})}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '15px'
                  }}
                >
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>

              <div style={{marginTop: '20px'}}>
                <label style={{display: 'block', fontWeight: '600', color: '#4b5563', marginBottom: '8px'}}>Comment</label>
                <textarea
                  rows={3}
                  value={active.wardenComment||''}
                  onChange={e=>setActive({...active,wardenComment:e.target.value})}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '15px',
                    fontFamily: 'inherit',
                    resize: 'vertical'
                  }}
                  placeholder="Add a comment (optional)"
                />
              </div>
            </div>
            <div style={{
              padding: '20px 24px',
              borderTop: '1px solid #e5e7eb',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
              background: '#f9fafb',
              borderRadius: '0 0 12px 12px'
            }}>
              <button 
                onClick={()=>setActive(null)} 
                disabled={saving}
                style={{
                  padding: '10px 24px',
                  border: '1px solid #d1d5db',
                  background: '#fff',
                  borderRadius: '8px',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  fontSize: '15px',
                  fontWeight: '500',
                  opacity: saving ? 0.5 : 1
                }}
              >
                Cancel
              </button>
              <button 
                onClick={save}
                disabled={saving}
                style={{
                  padding: '10px 24px',
                  border: 'none',
                  background: saving ? '#9ca3af' : '#3b82f6',
                  color: '#fff',
                  borderRadius: '8px',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  fontSize: '15px',
                  fontWeight: '600',
                  boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4)'
                }}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
          <style>{`
            @keyframes slideUp {
              from { 
                opacity: 0;
                transform: translateY(20px) scale(0.95);
              }
              to { 
                opacity: 1;
                transform: translateY(0) scale(1);
              }
            }
          `}</style>
        </div>
      )}
    </div>
  );
}
